import { defineStore } from "pinia";
import {
  CompaniesApi,
  AdminCompaniesApi,
  GroupsApi,
  type AdminCompanyListItem,
  type AdminCompaniesPaginationMeta,
} from "@simbelapp/order-sdk";
import type { ICompaniesStore } from "~/utils/interfaces/companies-interfaces";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { usePagination } from "~/composables/paginations/usePagination";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useAuthStore } from "~/store/auth/auth.store";
import { useFeedback } from "~/composables/feedback/useFeedback";

export const useCompaniesStore = defineStore("companies", {
  state: (): ICompaniesStore => {
    return {
      legalEntities: null,
      companyGroups: null,
      companyGroupsCamelCase: null,
      companyInfos: null,
      allCompanies: null,
      aiIsActivated: false,
      lmsProvider: null,
      contractTypes: null,
    };
  },
  getters: {},
  actions: {
    async fetchLegalEntities() {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
      const data = await apiInstance.getAllLegalEntities();

      if (data) this.legalEntities = data;
    },

    /**
     * Checks whether AI functionality is activated for the currently authenticated company.
     *
     * This asynchronous method retrieves the company's ID from the authentication store and
     * calls the API endpoint to verify if AI is activated. If the company ID is present, it updates
     * the "aiIsActivated" property based on the API response. In case of an error during the API call,
     * the error is logged to the console.
     *
     * @returns A promise that resolves to void.
     */
    async checkIfAIIsActivated(): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
      const authStore = useAuthStore();

      try {
        if (authStore?.jwtPayload?.companyId) {
          const response = await apiInstance.checkAIActivated({
            companyId: authStore.jwtPayload.companyId,
          });

          this.aiIsActivated = response?.aiActivated ?? false;
        }
      } catch (error) {
        console.error(error);
      }
    },

    async fetchCompanyGroupsCamelCase() {
      // by-roles already returns camelCase {groupId, name, type}; no lazy_load so all groups come back.
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, GroupsApi);
        const result = await apiInstance.getAvailableGroupsByRoles({ enableRolesFilters: false });
        if (result?.data) {
          this.companyGroupsCamelCase = result.data;
        }
      } catch {
        feedback.error(`erreur`, "small");
      }
    },

    async fetchCompanyInfos() {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
      const data = await apiInstance.getCompanyInfos();
      if (data) {
        this.companyInfos = data;
      }
    },

    /** Server-searched option source for the duplicate-company dropdown. */
    async searchAdminCompanies(
      params: Record<string, any> = {},
    ): Promise<{ data: AdminCompanyListItem[]; meta: AdminCompaniesPaginationMeta | null }> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);
        const response = await apiInstance.findAllCompanies(params);
        return { data: response.data ?? [], meta: response.meta };
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
        return { data: [], meta: null };
      }
    },

    async fetchAdminAllCompanies() {
      const pagination = usePagination();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, AdminCompaniesApi);

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      const result = await apiInstance.findAllCompanies({
        search: pagination.paginateOptions.value.search ?? "",
        ...paginateOptions,
      });

      if (result) {
        this.allCompanies = result;
      }
    },

    async fetchLmsProviders() {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
      const feedback = useFeedback();
      try {
        const data = await apiInstance.getLMSProvider();
        this.lmsProvider = data;
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    async getAllContractTypesByCompany(): Promise<void> {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, CompaniesApi);
      const feedback = useFeedback();

      try {
        const response = await apiInstance.getContractTypes();
        this.contractTypes = response ?? [];
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
  },
});
